<template>
  <div id="ai-chat" class="ai-chat-page">
    <!-- แถบบน: แชทใหม่ + ประวัติแชทส่วนตัว + (Admin) log ทุกสาขา -->
    <div class="topbar">
      <div class="topbar-left">
        <b-button variant="outline-primary" size="sm" @click="newChat">
          <i class="mdi mdi-plus"></i> แชทใหม่
        </b-button>
        <b-dropdown size="sm" variant="outline-secondary" text="ประวัติแชท" @show="fetchConversations">
          <b-dropdown-item v-if="conversations.length === 0" disabled>ยังไม่มีประวัติ</b-dropdown-item>
          <b-dropdown-item
            v-for="c in conversations"
            :key="c.id"
            :active="c.id === conversationId"
            @click="loadConversation(c.id)"
          >
            <div class="conv-item">
              <span class="conv-title">{{ c.title }}</span>
              <span class="conv-date">{{ formatDateTime(c.updated_at) }}</span>
              <span class="conv-del" title="ลบ" @click.stop="deleteConversation(c.id)">✕</span>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <b-button v-if="userRole === 'Admin'" variant="outline-secondary" size="sm" @click="openLogs">
        <i class="mdi mdi-history"></i> ประวัติการใช้งาน (ทุกสาขา)
      </b-button>
    </div>

    <!-- Modal ประวัติการใช้งาน -->
    <b-modal v-model="showLogs" title="ประวัติการใช้งาน AI พี่เสือ" size="xl" hide-footer>
      <div class="log-filters">
        <input type="date" v-model="logFilters.date_from" class="form-control form-control-sm" style="width:auto" @change="fetchLogs(1)" />
        <span>ถึง</span>
        <input type="date" v-model="logFilters.date_to" class="form-control form-control-sm" style="width:auto" @change="fetchLogs(1)" />
        <input type="text" v-model="logFilters.keyword" class="form-control form-control-sm" style="width:220px" placeholder="ค้นหาคำถาม/คำตอบ..." @keydown.enter="fetchLogs(1)" />
        <b-button size="sm" variant="primary" @click="fetchLogs(1)">ค้นหา</b-button>
      </div>

      <!-- สรุปต่อสาขา — คลิกเพื่อกรอง -->
      <div class="branch-chips">
        <button
          class="chip"
          :class="{ active: logFilters.branch_id === null }"
          @click="logFilters.branch_id = null; fetchLogs(1)"
        >ทุกสาขา</button>
        <button
          v-for="s in logSummary"
          :key="s.branch_id || 0"
          class="chip"
          :class="{ active: logFilters.branch_id === s.branch_id }"
          @click="logFilters.branch_id = s.branch_id; fetchLogs(1)"
        >{{ s.branch_name }} ({{ s.question_count }})</button>
      </div>

      <div v-if="logsLoading" class="text-center py-4">กำลังโหลด...</div>
      <div v-else-if="logRows.length === 0" class="text-center py-4 text-muted">ไม่พบประวัติในช่วงที่เลือก</div>
      <table v-else class="log-table">
        <thead>
          <tr><th style="width:130px">เวลา</th><th style="width:120px">สาขา</th><th style="width:140px">ผู้ถาม</th><th>คำถาม</th><th style="width:60px"></th></tr>
        </thead>
        <tbody>
          <template v-for="row in logRows">
            <tr :key="'r' + row.id" :class="{ 'row-error': row.is_error }" @click="toggleLogRow(row.id)" style="cursor:pointer">
              <td>{{ formatDateTime(row.created_at) }}</td>
              <td>{{ row.branch ? row.branch.name : '-' }}</td>
              <td>{{ row.user ? row.user.name : '#' + row.user_id }}</td>
              <td>{{ row.message }}</td>
              <td class="text-center">{{ expandedLogs.indexOf(row.id) >= 0 ? '▲' : '▼' }}</td>
            </tr>
            <tr v-if="expandedLogs.indexOf(row.id) >= 0" :key="'d' + row.id" class="log-detail">
              <td colspan="5">
                <div class="log-reply" v-html="renderMarkdown(row.reply || '-')"></div>
                <div class="log-meta">
                  <span v-if="row.tools">🔧 {{ row.tools }}</span>
                  <span v-if="row.duration_ms">⏱ {{ (row.duration_ms / 1000).toFixed(1) }}s</span>
                  <span v-if="row.is_error" class="text-danger">❌ error</span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="logHasMore" class="text-center mt-2">
        <b-button size="sm" variant="outline-primary" :disabled="logsLoading" @click="fetchLogs(logPage + 1, true)">โหลดเพิ่ม</b-button>
      </div>
    </b-modal>
    <!-- กล่องข้อความ -->
    <div class="chat-area" ref="chatArea">
      <div class="chat-inner">
        <!-- ข้อความต้อนรับ -->
        <div class="msg ai">
          <div class="avatar ai-logo"><img src="@/assets/favicon.png" alt="พี่เสือ" /></div>
          <div class="bubble-col">
            <div class="msg-name">พี่เสือ</div>
            <div class="bubble">
              สวัสดีครับ ผม<strong>พี่เสือ</strong> 🐯 ช่วยวิเคราะห์ข้อมูลขาย/CRM และร่างใบเสนอราคาได้ ลองถามได้เลย
              <div class="suggests">
                <button v-for="(s, i) in suggests" :key="i" type="button" @click="sendSuggest(s)">{{ s }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- บทสนทนา -->
        <div v-for="(m, i) in messages" :key="'m' + i" class="msg" :class="m.role">
          <div v-if="m.role === 'ai'" class="avatar ai-logo"><img src="@/assets/favicon.png" alt="พี่เสือ" /></div>
          <div v-else class="avatar">คุณ</div>
          <div class="bubble-col">
          <div v-if="m.role === 'ai'" class="msg-name">พี่เสือ</div>
          <div class="bubble">
            <div v-if="m.role === 'ai'" v-html="renderMarkdown(m.text)"></div>
            <div v-else class="user-text">{{ m.text }}</div>
            <div v-if="m.tools && m.tools.length" class="tools-chip">🔍 ดึงข้อมูลจาก: {{ m.tools.join(', ') }}</div>

            <!-- การ์ดร่างใบเสนอราคา -->
            <div v-if="m.draft" class="draft-card">
              <div class="draft-head">
                📄 ร่างใบเสนอราคา
                <span class="draft-status" :class="'st-' + m.draft.state">{{ draftStatusLabel(m.draft) }}</span>
              </div>
              <div class="draft-customer">ลูกค้า: <strong>{{ m.draft.customer.name }}</strong>
                <template v-if="m.draft.customer.company_name"> ({{ m.draft.customer.company_name }})</template>
              </div>
              <table class="draft-items">
                <thead>
                  <tr><th>รายการ</th><th class="num">จำนวน</th><th class="num">ราคา/หน่วย</th><th class="num">รวม</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(it, j) in m.draft.items" :key="j">
                    <td>
                      {{ it.description }}
                      <span v-if="it.sku_code" class="sku-tag">{{ it.sku_code }}</span>
                    </td>
                    <td class="num">{{ formatNum(it.quantity) }}</td>
                    <td class="num">{{ formatMoney(it.unit_price) }}</td>
                    <td class="num">{{ formatMoney(it.total_price) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr><td colspan="3">รวม</td><td class="num">{{ formatMoney(m.draft.subtotal) }}</td></tr>
                  <tr v-if="m.draft.discount > 0"><td colspan="3">ส่วนลด</td><td class="num">-{{ formatMoney(m.draft.discount) }}</td></tr>
                  <tr v-if="m.draft.vat_amount > 0"><td colspan="3">VAT 7%</td><td class="num">{{ formatMoney(m.draft.vat_amount) }}</td></tr>
                  <tr class="grand"><td colspan="3">ยอดสุทธิ</td><td class="num">{{ formatMoney(m.draft.grand_total) }}</td></tr>
                </tfoot>
              </table>
              <div v-if="m.draft.state === 'pending'" class="draft-actions">
                <b-button variant="success" size="sm" :disabled="confirming" @click="confirmDraft(m.draft)">
                  ✓ ยืนยันสร้างใบเสนอราคา
                </b-button>
                <b-button variant="outline-secondary" size="sm" :disabled="confirming" @click="m.draft.state = 'cancelled'">
                  ยกเลิก
                </b-button>
                <span class="draft-note">สร้างเป็น "ร่าง" — ไปตรวจ/อนุมัติในหน้าเอกสารตามปกติ</span>
              </div>
              <div v-else-if="m.draft.state === 'confirmed'" class="draft-confirmed">
                ✅ สร้างแล้ว เลขที่ <strong>{{ m.draft.quotation_no }}</strong> (สถานะร่าง)
              </div>
            </div>
          </div>
          </div>
        </div>

        <!-- กำลังพิมพ์ -->
        <div v-if="loading" class="msg ai">
          <div class="avatar ai-logo"><img src="@/assets/favicon.png" alt="พี่เสือ" /></div>
          <div class="bubble-col">
            <div class="msg-name">พี่เสือ</div>
            <div class="bubble"><span class="typing"><span></span><span></span><span></span></span></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ช่องพิมพ์ -->
    <div class="input-bar">
      <div class="input-row">
        <textarea
          ref="inputBox"
          v-model="input"
          class="chat-input form-control"
          rows="1"
          :placeholder="listening ? '🎙️ กำลังฟัง... พูดได้เลย' : 'ถามยอดขาย เป้า ลูกหนี้ หรือสั่งร่างใบเสนอราคา...'"
          @keydown.enter.exact.prevent="send"
          @input="autoGrow"
          :disabled="loading"
        ></textarea>
        <button
          v-if="speechSupported"
          type="button"
          class="mic-btn"
          :class="{ listening: listening }"
          :disabled="loading"
          :title="listening ? 'หยุดฟัง' : 'พูดแทนพิมพ์'"
          @click="toggleMic"
        >
          <i class="mdi" :class="listening ? 'mdi-stop' : 'mdi-microphone'"></i>
        </button>
        <b-button variant="primary" :disabled="loading || !input.trim()" @click="send">ส่ง</b-button>
      </div>
      <div class="disclaimer">ตัวเลขดึงจากฐานข้อมูลจริงตามสิทธิ์ของคุณ · ใบเสนอราคาที่สร้างเป็นสถานะร่างเสมอ</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AiChat",
  data() {
    return {
      input: "",
      loading: false,
      confirming: false,
      listening: false,
      speechSupported: false,
      userRole: null,
      showLogs: false,
      logsLoading: false,
      logSummary: [],
      logRows: [],
      logPage: 1,
      logHasMore: false,
      expandedLogs: [],
      logFilters: { date_from: "", date_to: "", branch_id: null, keyword: "" },
      messages: [], // {role: 'user'|'ai', text, tools?, draft?}
      conversationId: null, // บทสนทนาปัจจุบัน — history ประกอบฝั่ง server จาก id นี้
      conversations: [],
      suggests: [
        "ยอดขายเดือนนี้เท่าไหร่",
        "เทียบเป้ากับยอดจริงเดือนนี้",
        "ลูกค้า 5 อันดับที่ซื้อเยอะสุด",
        "เดือนนี้เก็บเงินได้เท่าไหร่แล้ว",
      ],
    };
  },
  async mounted() {
    // Web Speech API — Chrome/Edge/Safari (ต้อง HTTPS หรือ localhost)
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.speechSupported = !!SR;
    // role สำหรับโชว์ปุ่ม log (Admin เท่านั้น) — ใช้ store เดียวกับ Sidebar
    try {
      const res = await this.$store.dispatch("Auth/getRole", {}, { root: true });
      this.userRole = res.data.role;
    } catch (e) {
      this.userRole = null;
    }
  },
  beforeDestroy() {
    this.stopMic();
  },
  methods: {
    newChat() {
      this.stopMic();
      this.messages = [];
      this.conversationId = null;
      this.clearInput();
    },
    async fetchConversations() {
      try {
        const res = await this.$axios.get("/api/v1/chatbot/conversations");
        this.conversations = res.data.conversations || [];
      } catch (e) {
        /* เงียบได้ — dropdown ว่างเฉย ๆ */
      }
    },
    async loadConversation(id) {
      try {
        const res = await this.$axios.get("/api/v1/chatbot/conversations/" + id);
        const msgs = [];
        for (const t of res.data.turns) {
          msgs.push({ role: "user", text: t.message });
          if (t.reply) {
            msgs.push({
              role: "ai",
              text: (t.is_error ? "⚠️ " : "") + t.reply,
              tools: t.tools ? t.tools.split(",") : [],
            });
          }
        }
        this.messages = msgs;
        this.conversationId = res.data.id;
        this.scrollDown();
      } catch (e) {
        this.$swal({ icon: "error", title: "โหลดบทสนทนาไม่สำเร็จ" });
      }
    },
    async deleteConversation(id) {
      try {
        await this.$axios.delete("/api/v1/chatbot/conversations/" + id);
        this.conversations = this.conversations.filter((c) => c.id !== id);
        if (this.conversationId === id) this.newChat();
      } catch (e) {
        this.$swal({ icon: "error", title: "ลบไม่สำเร็จ" });
      }
    },
    openLogs() {
      this.showLogs = true;
      if (this.logRows.length === 0) this.fetchLogs(1);
    },
    async fetchLogs(page, append) {
      this.logsLoading = !append;
      try {
        const params = { page: page || 1 };
        if (this.logFilters.date_from) params.date_from = this.logFilters.date_from;
        if (this.logFilters.date_to) params.date_to = this.logFilters.date_to;
        if (this.logFilters.branch_id !== null) params.branch_id = this.logFilters.branch_id;
        if (this.logFilters.keyword) params.keyword = this.logFilters.keyword;
        const res = await this.$axios.get("/api/v1/chatbot/logs", { params });
        this.logSummary = res.data.summary || [];
        const pg = res.data.logs;
        this.logRows = append ? this.logRows.concat(pg.data) : pg.data;
        this.logPage = pg.current_page;
        this.logHasMore = pg.current_page < pg.last_page;
        if (!append) this.expandedLogs = [];
      } catch (e) {
        this.$swal({ icon: "error", title: "โหลดประวัติไม่สำเร็จ" });
      } finally {
        this.logsLoading = false;
      }
    },
    toggleLogRow(id) {
      const i = this.expandedLogs.indexOf(id);
      if (i >= 0) this.expandedLogs.splice(i, 1);
      else this.expandedLogs.push(id);
    },
    formatDateTime(s) {
      const d = new Date(s);
      return d.toLocaleDateString("th-TH", { day: "numeric", month: "short" }) +
        " " + d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
    },
    toggleMic() {
      if (this.listening) {
        this.stopMic();
        return;
      }
      this.listening = true;
      this.startRecognition();
    },
    startRecognition() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) return;

      const rec = new SR();
      rec.lang = "th-TH";
      rec.interimResults = true; // โชว์ข้อความระหว่างพูดแบบ real-time
      rec.continuous = true;     // ฟังต่อเนื่องหลายประโยค — หยุดเมื่อกดปุ่ม stop เท่านั้น
      // จุดต่อข้อความของ session นี้ (ข้อความเดิม/ประโยคก่อนหน้า)
      this._baseInput = this.input ? this.input.replace(/\s+$/, "") + " " : "";

      rec.onresult = (e) => {
        if (!this.listening) return; // ผลตกค้างหลังสั่งหยุด — ทิ้ง ไม่งั้นเขียนทับช่องที่เคลียร์แล้ว
        let transcript = "";
        for (let i = 0; i < e.results.length; i++) {
          transcript += e.results[i][0].transcript;
        }
        this.input = this._baseInput + transcript;
        this.$nextTick(this.autoGrow);
      };
      rec.onend = () => {
        // browser ตัดเองตอนเงียบนาน — ถ้าผู้ใช้ยังไม่กด stop ให้ต่อไมค์อัตโนมัติ
        if (this.listening) {
          this.startRecognition();
        } else {
          this._recognition = null;
          const el = this.$refs.inputBox;
          if (el) el.focus();
        }
      };
      rec.onerror = (e) => {
        if (e.error === "no-speech" || e.error === "aborted") return; // onend จะจัดการต่อเอง
        this.listening = false;
        this._recognition = null;
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          this.$swal({
            icon: "warning",
            title: "ใช้ไมค์ไม่ได้",
            text: "กรุณาอนุญาตการใช้ไมโครโฟนใน browser (ไอคอนรูปกุญแจ/ไมค์ ข้าง URL)",
          });
        }
      };

      this._recognition = rec;
      try {
        rec.start();
      } catch (e) {
        this.listening = false;
        this._recognition = null;
      }
    },
    stopMic() {
      this.listening = false; // ตั้งก่อน stop — กัน onend เผลอ restart
      if (this._recognition) {
        const rec = this._recognition;
        this._recognition = null;
        // ถอด handler ก่อน abort — กัน event ตกค้าง (onresult/onend) ยิงหลังเคลียร์ช่องพิมพ์
        rec.onresult = null;
        rec.onend = null;
        rec.onerror = null;
        try { rec.abort(); } catch (e) { /* already stopped */ }
      }
    },
    sendSuggest(s) {
      this.input = s;
      this.send();
    },
    autoGrow() {
      const el = this.$refs.inputBox;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 140) + "px";
    },
    clearInput() {
      this.input = "";
      // กันเหนียว: เคลียร์ DOM ตรง ๆ ด้วย แล้วย่อช่องกลับขนาดเดิม
      const el = this.$refs.inputBox;
      if (el) {
        el.value = "";
        el.style.height = "auto";
      }
    },
    async send() {
      this.stopMic(); // กดส่งระหว่างไมค์เปิด → ปิดไมค์ก่อน
      const text = this.input.trim();
      if (!text || this.loading) return;
      this.clearInput();
      this.messages.push({ role: "user", text });
      this.loading = true;
      this.scrollDown();

      try {
        const res = await this.$axios.post("/api/v1/chatbot/chat", {
          message: text,
          conversation_id: this.conversationId, // null = เริ่มบทสนทนาใหม่ (server สร้างให้)
        });
        const d = res.data;
        this.messages.push({
          role: "ai",
          text: d.reply || "",
          tools: d.tools_used || [],
          draft: d.draft ? Object.assign({}, d.draft, { state: "pending" }) : null,
        });
        if (d.conversation_id) this.conversationId = d.conversation_id;
      } catch (e) {
        const msg =
          e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : "เชื่อมต่อไม่ได้ กรุณาลองใหม่";
        this.messages.push({ role: "ai", text: "⚠️ " + msg });
      } finally {
        this.loading = false;
        this.scrollDown();
        this.$nextTick(() => {
          const el = this.$refs.inputBox;
          if (el) el.focus();
        });
      }
    },
    async confirmDraft(draft) {
      this.confirming = true;
      try {
        const res = await this.$axios.post("/api/v1/chatbot/confirm-draft", {
          draft_id: draft.draft_id,
        });
        draft.state = "confirmed";
        draft.quotation_no = res.data.quotation_no;
        this.$swal({
          icon: "success",
          title: "สร้างใบเสนอราคาสำเร็จ",
          text: "เลขที่ " + res.data.quotation_no + " (สถานะร่าง)",
        });
      } catch (e) {
        const msg =
          e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : "สร้างไม่สำเร็จ กรุณาลองใหม่";
        if (e.response && e.response.status === 410) draft.state = "expired";
        this.$swal({ icon: "error", title: "ไม่สำเร็จ", text: msg });
      } finally {
        this.confirming = false;
      }
    },
    draftStatusLabel(draft) {
      if (draft.state === "confirmed") return "สร้างแล้ว";
      if (draft.state === "cancelled") return "ยกเลิกแล้ว";
      if (draft.state === "expired") return "หมดอายุ";
      return "รอยืนยัน";
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.chatArea;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    formatMoney(n) {
      return Number(n || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatNum(n) {
      return Number(n || 0).toLocaleString("th-TH");
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    // markdown แบบย่อ: **bold**, ตาราง, bullet — พอสำหรับคำตอบ AI
    renderMarkdown(text) {
      const lines = String(text || "").split("\n");
      let html = "";
      let inTable = false;
      let inList = false;
      let isHeader = false;
      const inline = (s) =>
        this.escapeHtml(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      const isNum = (s) => /^[\d,.\s%฿+-]+$/.test(s.trim()) && /\d/.test(s);
      const closeAll = () => {
        if (inTable) { html += "</table>"; inTable = false; }
        if (inList) { html += "</ul>"; inList = false; }
      };
      for (const line of lines) {
        if (/^\s*\|.*\|\s*$/.test(line)) {
          if (/^\s*\|[\s\-:|]+\|\s*$/.test(line)) continue;
          const cells = line.trim().replace(/^\||\|$/g, "").split("|");
          if (!inTable) { closeAll(); html += '<table class="md-table">'; inTable = true; isHeader = true; }
          else isHeader = false;
          html += "<tr>";
          for (const c of cells) {
            const tag = isHeader ? "th" : "td";
            html += `<${tag}${isNum(c) ? ' class="num"' : ""}>${inline(c.trim())}</${tag}>`;
          }
          html += "</tr>";
        } else if (/^\s*[-*•]\s+/.test(line)) {
          if (inTable) { html += "</table>"; inTable = false; }
          if (!inList) { html += "<ul>"; inList = true; }
          html += "<li>" + inline(line.replace(/^\s*[-*•]\s+/, "")) + "</li>";
        } else {
          closeAll();
          if (line.trim() !== "") html += "<p>" + inline(line) + "</p>";
        }
      }
      closeAll();
      return html;
    },
  },
};
</script>

<style scoped>
.ai-chat-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
  background: #f4f5f7;
}
.topbar { padding: 8px 16px 0; display: flex; justify-content: space-between; align-items: center; }
.topbar-left { display: flex; gap: 8px; align-items: center; }
.conv-item { display: flex; align-items: center; gap: 10px; max-width: 340px; }
.conv-title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.conv-date { font-size: 11px; color: #8a8a95; flex-shrink: 0; }
.conv-del { color: #bbb; font-size: 12px; padding: 0 2px; flex-shrink: 0; }
.conv-del:hover { color: #dc3545; }

/* ---------- log viewer ---------- */
.log-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 10px; }
.branch-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
.chip {
  border: 1px solid #e4e4ec; background: #fff; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; cursor: pointer;
}
.chip.active { background: #6f42c1; border-color: #6f42c1; color: #fff; }
.log-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.log-table th, .log-table td { border-bottom: 1px solid #eee; padding: 7px 9px; text-align: left; vertical-align: top; }
.log-table thead th { background: #f7f5fc; position: sticky; top: 0; }
.log-table tr.row-error td { background: #fff5f5; }
.log-detail td { background: #faf9fd; }
.log-reply { font-size: 13px; max-height: 260px; overflow-y: auto; }
.log-meta { margin-top: 6px; font-size: 11.5px; color: #8a8a95; display: flex; gap: 14px; }
.chat-area { flex: 1; overflow-y: auto; padding: 24px 16px; }
.chat-inner { max-width: 860px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
.msg { display: flex; gap: 10px; }
.msg .avatar {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; font-size: 12px;
  display: flex; align-items: center; justify-content: center; font-weight: 700;
}
.msg.ai .avatar { background: #6f42c1; color: #fff; }
.avatar.ai-logo {
  background: #fff; border: 1px solid #e4e4ec; border-radius: 50%; overflow: hidden;
}
.avatar.ai-logo img { width: 100%; height: 100%; object-fit: cover; }
.bubble-col { max-width: 85%; display: flex; flex-direction: column; }
.bubble-col .bubble { max-width: 100%; }
.msg-name { font-size: 11.5px; color: #8a8a95; margin: 0 0 3px 4px; }
.msg.user { flex-direction: row-reverse; }
.msg.user .avatar { background: #2f4a43; color: #fff; }
.bubble {
  max-width: 85%; padding: 12px 16px; border-radius: 14px; font-size: 14.5px; line-height: 1.65;
}
.msg.ai .bubble { background: #fff; border: 1px solid #e4e4ec; border-top-left-radius: 4px; }
.msg.user .bubble { background: #2f4a43; color: #fff; border-top-right-radius: 4px; }
.user-text { white-space: pre-wrap; }
.bubble >>> .md-table { border-collapse: collapse; margin: 10px 0; width: 100%; font-size: 13.5px; }
.bubble >>> .md-table th, .bubble >>> .md-table td { border: 1px solid #e4e4ec; padding: 6px 10px; text-align: left; }
.bubble >>> .md-table th { background: #f3eefc; }
.bubble >>> .md-table td.num, .bubble >>> .md-table th.num { text-align: right; }
.bubble >>> ul { padding-left: 22px; margin: 8px 0; }
.bubble >>> p { margin: 6px 0; }
.tools-chip { margin-top: 8px; font-size: 11.5px; color: #8a8a95; }
.typing { display: inline-flex; gap: 4px; padding: 4px 0; }
.typing span {
  width: 7px; height: 7px; border-radius: 50%; background: #8a8a95; opacity: .4;
  animation: blink 1.2s infinite;
}
.typing span:nth-child(2) { animation-delay: .2s; }
.typing span:nth-child(3) { animation-delay: .4s; }
@keyframes blink { 0%, 80%, 100% { opacity: .25 } 40% { opacity: 1 } }
.suggests { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.suggests button {
  border: 1px solid #e4e4ec; background: #fff; border-radius: 999px;
  padding: 6px 14px; font-size: 13px; cursor: pointer;
}
.suggests button:hover { border-color: #6f42c1; color: #6f42c1; }

/* ---------- draft card ---------- */
.draft-card {
  margin-top: 12px; border: 1px solid #d9d2ec; border-radius: 12px;
  background: #faf8ff; padding: 14px 16px;
}
.draft-head { font-weight: 700; font-size: 14px; display: flex; align-items: center; gap: 8px; }
.draft-status { font-size: 11px; font-weight: 600; padding: 2px 10px; border-radius: 999px; }
.draft-status.st-pending { background: #fff3cd; color: #856404; }
.draft-status.st-confirmed { background: #d4edda; color: #155724; }
.draft-status.st-cancelled, .draft-status.st-expired { background: #eee; color: #777; }
.draft-customer { margin: 8px 0 6px; font-size: 13.5px; }
.draft-items { width: 100%; border-collapse: collapse; font-size: 13px; margin: 6px 0 10px; }
.draft-items th, .draft-items td { border: 1px solid #e2dcf2; padding: 5px 9px; }
.draft-items th { background: #efe9fb; }
.draft-items .num { text-align: right; }
.draft-items tfoot td { font-weight: 500; }
.draft-items tr.grand td { font-weight: 700; background: #efe9fb; }
.sku-tag {
  display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px;
  background: #efe9fb; color: #6f42c1; font-size: 11px; font-weight: 600;
}
.draft-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.draft-note { font-size: 11.5px; color: #8a8a95; }
.draft-confirmed { font-size: 13.5px; color: #155724; }

/* ---------- input ---------- */
.input-bar { background: #fff; border-top: 1px solid #e4e4ec; padding: 12px 16px; }
.input-row { max-width: 860px; margin: 0 auto; display: flex; gap: 10px; align-items: flex-end; }
.chat-input { flex: 1; resize: none; overflow-y: auto; max-height: 140px; line-height: 1.5; }
.mic-btn {
  width: 42px; height: 38px; flex-shrink: 0; border-radius: 8px;
  border: 1px solid #ced4da; background: #fff; color: #555;
  font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.mic-btn:hover { border-color: #6f42c1; color: #6f42c1; }
.mic-btn:disabled { opacity: .5; cursor: not-allowed; }
.mic-btn.listening {
  background: #dc3545; border-color: #dc3545; color: #fff;
  animation: mic-pulse 1.4s infinite;
}
@keyframes mic-pulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, .45); }
  70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
}
.disclaimer { max-width: 860px; margin: 6px auto 0; font-size: 11.5px; color: #8a8a95; text-align: center; }
</style>
